import {RefObject, useEffect, useState} from "react";

import {useSnapshot} from "valtio";

import {type GuestsStepperInstance} from "@/features/rental";

import {useDetectClickOutside} from "@/shared/hooks/dom/use-detect-click-outside";

interface UseGuestsDropdownOptions {
    model: GuestsStepperInstance;
}

interface UseGuestsDropdownReturn {
    snapshot: ReturnType<typeof useSnapshot<GuestsStepperInstance>>;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    dropdownRef: RefObject<HTMLDivElement | null>;
    handleReset: () => void;
    handleApply: () => void;
    handleToggle: () => void;
}

export function useGuestsDropdown({model}: UseGuestsDropdownOptions): UseGuestsDropdownReturn {
    const snapshot = useSnapshot(model);
    const [isOpen, setIsOpen] = useState(false);

    // Close dropdown when clicking/tapping outside
    const dropdownRef = useDetectClickOutside<HTMLDivElement>({
        onTriggered: () => {
            if (isOpen) {
                setIsOpen(false);
                model.apply();
            }
        },
    });

    useEffect(() => {
        const open = (): void => setIsOpen(true);
        model.events.on("open", open);

        return () => model.events.off("open", open);
    }, [model]);
    const handleReset = (): void => {
        setIsOpen(false);
        model.reset();
    };

    const handleApply = (): void => {
        setIsOpen(false);
        model.apply();
    };

    const handleToggle = (): void => {
        setIsOpen(!isOpen);
    };

    return {
        snapshot,
        isOpen,
        setIsOpen,
        dropdownRef,
        handleReset,
        handleApply,
        handleToggle,
    };
}
